import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { JAR_PATTERN } from '../utils/constants';
import BalanceInput from './BalanceInput';
import ChangesList from './ChangesList';
import styles from './BalanceTracker.module.css';
import { fetchBalanceChanges, deactivateTrack } from '../services/balanceService';
import { saveToLocalStorage, readFromLocalStorage } from '../utils/localStorageHelper';
import { setMetaTheme } from '../utils/metaHelper';
import useActiveTab from '../hooks/useActiveTab';

const FETCH_INTERVAL = 60;
const FETCH_INTERVAL_MS = FETCH_INTERVAL * 1000;

function BalanceTracker() {
  const { balanceId: urlBalanceId } = useParams();
  const navigate = useNavigate();
  const [balanceId, setBalanceId] = useState(urlBalanceId || '');
  const [changes, setChanges] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const isTabActive = useActiveTab();
  const [recentId, setRecentId] = useState(0);
  const [previousRecentId, setPreviousRecentId] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleFetchChanges = useCallback(
    async (force = false) => {
      try {
        setLoading(true);
        const recent = readFromLocalStorage('recent-incoming', balanceId);
        setPreviousRecentId(recent);
        setRecentId(recent);
        const data = await fetchBalanceChanges(balanceId, force);
        setChanges(data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error('Failed to fetch balance changes:', error);
      }
    },
    [balanceId]
  );

  useEffect(() => {
    let interval;

    if (balanceId && isTabActive) {
      handleFetchChanges();
      interval = setInterval(() => {
        handleFetchChanges();
      }, FETCH_INTERVAL_MS);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [balanceId, handleFetchChanges, isTabActive]);

  useEffect(() => {
    if (changes?.jar?.title) {
      document.title = `${changes.jar.title} - Jar Tracker`;
    } else {
      document.title = 'Jar Tracker';
    }
  }, [changes?.jar?.title]);

  useEffect(() => {
    if (!changes?.incoming?.length || !changes?.account?.trackId) {
      return;
    }

    setPreviousRecentId(recentId);
    const recent = changes?.incoming?.[0].id;
    setRecentId(recent);
    saveToLocalStorage('recent-incoming', recent, changes?.account?.trackId);
  }, [changes?.incoming?.[0]?.id]);

  useEffect(() => {
    const isClosed = changes?.jar?.status?.toLowerCase() === 'closed';
    setIsCompleted(isClosed);

    if (isClosed && changes?.account?.isActive && changes?.account?.trackId) {
      deactivateTrack(changes.account.trackId).catch(error => {
        console.error('Failed to deactivate completed jar track:', error);
      });
    }
  }, [changes?.jar?.status, changes?.account?.isActive, changes?.account?.trackId]);

  useEffect(() => {
    if (!loading) {
      setMetaTheme(isCompleted ? '#0057a4' : '#0d8638');
    }
  }, [isCompleted, loading]);

  useEffect(() => {
    if (loading) {
      setMetaTheme('#75847a');
    }
  }, [loading]);

  const handleStartTracking = input => {
    let id = input;
    const match = input.match(JAR_PATTERN);

    if (match) {
      id = match[3];
    }

    if (balanceId !== id) {
      setChanges({});
    }
    setBalanceId(id);
    navigate(`/${id}`);
  };

  const toAmount = value => {
    return `${Math.floor(value / 100).toLocaleString('uk-UK')} грн.`;
  };

  const jarProgress =
    changes?.jar?.balance &&
    changes?.jar?.goal &&
    Math.min((changes.jar.balance / changes.jar.goal) * 100, 100);

  const reactivate = () => {
    handleFetchChanges(true);
  };

  const needsReactivation = !isCompleted && changes?.account?.isActive === false;

  return (
    <div className="space-y-4">
      {changes?.jar?.ownerName ? (
        <div className={styles['title']}>
          <div className={styles['owner-name']}>{changes?.jar?.ownerName}</div>

          <div className={styles['jar-wrapper']}>
            <div className={styles['jar-details']}>
              <span>
                <a
                  href={`https://send.monobank.ua/jar/${balanceId}`}
                  target="_blank"
                  className={styles['jar-title']}
                  rel="noreferrer"
                >
                  {changes?.jar?.title}
                </a>
                {isCompleted && <span className={styles['jar-status-completed']}>[розбита ✓]</span>}

                {needsReactivation && (
                  <span className={styles['jar-status-inactive']} onClick={reactivate}>
                    [неактивна ↻]
                  </span>
                )}
              </span>
              {changes?.jar?.balance && (
                <div
                  className={`${styles['jar-balance']} ${isCompleted && styles['jar-balance-completed']} ${loading && styles['jar-balance-loading']}`}
                >
                  {toAmount(changes.jar.balance)}
                  {!!changes?.jar?.goal && (
                    <span className={styles['jar-goal']}>/ {toAmount(changes.jar.goal)}</span>
                  )}
                </div>
              )}
            </div>

            {jarProgress && (
              <div className={styles['jar-progress-wrapper']}>
                <div
                  className={`${styles['jar-progress']} ${isCompleted && styles['jar-progress-completed']} ${loading && styles['jar-progress-loading']}`}
                  style={{ width: `${jarProgress}%` }}
                ></div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <h2 className={styles['title']}>Трекер банки</h2>
      )}
      <BalanceInput
        onSubmit={handleStartTracking}
        initialValue={urlBalanceId || ''}
        loading={loading}
      />
      <ChangesList changes={changes.incoming} recentIncomingId={previousRecentId} />
    </div>
  );
}

export default BalanceTracker;
